import { useState } from 'react';

import EmojiPickerSheet from '@/components/EmojiPickerSheet';
import { EMOJI_GROUPS, type EmojiGroupId } from '@/constants/emojis';

/** Jump-strip icon per Unicode category — the circle catalogue's own grouping. */
const CIRCLE_CATEGORY_ICONS: Record<EmojiGroupId, string> = {
  smileys: '😀',
  gestures: '👍',
  hearts: '❤️',
  music: '🎵',
  animals: '🐰',
  nature: '🌸',
  food: '🍕',
  travel: '🚀',
  objects: '💯',
};
import { ButtonSystem } from '@/components/Buttons';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/utils/i18n';

/**
 * "Image qui vous ressemble" — the circle emoji field, shared by circle creation
 * and circle management (C3b).
 *
 * It replaces the 4-row strip of ~90 curated emojis: the full catalogue is far
 * too long to scroll horizontally, so the field shows the current emoji and
 * opens the same bottom sheet the reaction picker uses.
 *
 * The emoji sits in a fixed 44x44 square tile (the former BackButton chrome)
 * so it reads as a preview, not as a text input — and the action lives in an
 * explicit secondary button next to it.
 */

/** Shown when a circle has no emoji yet — the same default circle creation uses. */
const DEFAULT_CIRCLE_EMOJI = '⭐';

export default function CircleEmojiField({
  value,
  onChange,
  disabled = false,
  surfaceClass = 'tw:bg-dark-1',
}: {
  /** `null` when the circle was created before an emoji was required. */
  value: string | null;
  onChange: (emoji: string) => void;
  disabled?: boolean;
  /** Tile background — screens do not all use the same input surface. */
  surfaceClass?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <label className="tw:text-[13px] tw:font-semibold tw:text-grey-2">
        {t('groups.create.emojiLabel')}
      </label>

      <div className="tw:flex tw:items-center tw:gap-3">
        <div
          data-testid="circle-emoji-current"
          className={cn(
            'tw:flex tw:size-11 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-2xl tw:border tw:border-blue-2/40 tw:text-[24px] tw:leading-none',
            surfaceClass,
            disabled && 'tw:opacity-60',
          )}
        >
          {value ?? DEFAULT_CIRCLE_EMOJI}
        </div>

        {!disabled && (
          <div className="tw:shrink-0">
            <ButtonSystem
              size="s"
              variant="secondary"
              label={t('groups.create.emojiChange')}
              onClick={() => setOpen(true)}
              data-testid="btn-circle-emoji"
            />
          </div>
        )}
      </div>

      {open && (
        <EmojiPickerSheet
          // reaction-emoji-signal — circles keep the WIDE catalogue: a circle
          // called "Les Paons" may be 🦚. Only reactions were narrowed.
          sections={EMOJI_GROUPS.map((g) => ({
            id: g.id,
            icon: CIRCLE_CATEGORY_ICONS[g.id],
            label: t(`emojiPicker.categories.${g.id}`),
            emojis: g.emojis,
          }))}
          current={value}
          title={t('emojiPicker.circleTitle')}
          testIdPrefix="circle"
          onPick={(emoji) => {
            onChange(emoji);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
